import React, { useState, useMemo } from "react";

// ═════════════════════════════════════════════════════════════════════════════
// ZONE MANAGEMENT DASHBOARD - Professional UI (No Icons, Clean Typography)
// ═════════════════════════════════════════════════════════════════════════════

const ZoneManagement = ({ isDark = false }) => {
  const theme = isDark ? {
    bg: "#0f1419",
    surface: "#1a1f26",
    border: "#282e38",
    text: "#e4e6eb",
    textSecondary: "#8a8d91",
    textMuted: "#5c6066",
    primary: "#0a66c2",
    primaryLight: "#0a66c250",
    success: "#31a24c",
    successLight: "#31a24c20",
    danger: "#e85d75",
    dangerLight: "#e85d7520",
    warning: "#f5a623",
    warningLight: "#f5a62320",
  } : {
    bg: "#ffffff",
    surface: "#f8f9fa",
    border: "#e5e7eb",
    text: "#1a1a1a",
    textSecondary: "#6b7280",
    textMuted: "#9ca3af",
    primary: "#0a66c2",
    primaryLight: "#0a66c215",
    success: "#31a24c",
    successLight: "#31a24c15",
    danger: "#e85d75",
    dangerLight: "#e85d7515",
    warning: "#f5a623",
    warningLight: "#f5a62315",
  };

  // Mock data - Replace with real data from API
  const mockZones = [
    { id: 1, name: "Office", status: "allowed", active: true, points: 5 },
    { id: 2, name: "Home", status: "allowed", active: true, points: 7 },
    { id: 3, name: "Gwalior Main City", status: "allowed", active: true, points: 9 },
    { id: 4, name: "Delhi", status: "allowed", active: false, points: 8 },
    { id: 5, name: "Downtown", status: "blacklisted", active: false, points: 4 },
    { id: 6, name: "Airport Zone", status: "blacklisted", active: false, points: 6 },
    { id: 7, name: "Market Area", status: "allowed", active: true, points: 5 },
  ];

  const [zones, setZones] = useState(mockZones);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [mapCenter] = useState({ lat: 26.2389, lng: 75.8659 });

  // Filter zones
  const filteredZones = useMemo(() => {
    let filtered = zones;
    if (activeTab === "allowed") {
      filtered = filtered.filter(z => z.status === "allowed");
    } else if (activeTab === "blacklisted") {
      filtered = filtered.filter(z => z.status === "blacklisted");
    } else if (activeTab === "active") {
      filtered = filtered.filter(z => z.active);
    }
    if (searchTerm) {
      filtered = filtered.filter(z => z.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    return filtered;
  }, [zones, activeTab, searchTerm]);

  // Calculate stats
  const stats = useMemo(() => ({
    total: zones.length,
    allowed: zones.filter(z => z.status === "allowed").length,
    blacklisted: zones.filter(z => z.status === "blacklisted").length,
    active: zones.filter(z => z.active).length,
  }), [zones]);

  const handleDeleteZone = (id) => {
    if (window.confirm("Delete this zone?")) {
      setZones(zones.filter(z => z.id !== id));
    }
  };

  const handleAddZone = () => {
    alert("Add Zone clicked - Implement navigation to add zone form");
  };

  const StatCard = ({ label, value, color }) => (
    <div style={{
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: "10px",
      padding: "14px 18px",
      flex: 1,
      minWidth: "100px",
    }}>
      <div style={{ fontSize: "11px", color: theme.textSecondary, marginBottom: "6px", letterSpacing: "0.3px" }}>
        {label}
      </div>
      <div style={{ fontSize: "28px", fontWeight: 700, color: color }}>
        {value}
      </div>
    </div>
  );

  const TabButton = ({ label, id, count }) => (
    <button
      onClick={() => setActiveTab(id)}
      style={{
        padding: "7px 16px",
        background: activeTab === id ? theme.primary : "transparent",
        color: activeTab === id ? "#ffffff" : theme.textSecondary,
        border: activeTab === id ? "none" : `1px solid ${theme.border}`,
        borderRadius: "8px",
        cursor: "pointer",
        fontSize: "12px",
        fontWeight: 500,
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
      }}
    >
      {label}
      {count !== undefined && (
        <span style={{ 
          fontSize: "11px", 
          opacity: 0.7,
          fontWeight: 400,
        }}>
          ({count})
        </span>
      )}
    </button>
  );

  const ZoneCard = ({ zone }) => (
    <div style={{
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: "10px",
      padding: "12px 14px",
      marginBottom: "10px",
      transition: "all 0.2s",
    }}>
      {/* Zone Header */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "flex-start", 
        marginBottom: "10px",
        borderLeft: `3px solid ${zone.status === "allowed" ? theme.success : theme.danger}`,
        paddingLeft: "10px",
      }}>
        <div>
          <h3 style={{ 
            fontSize: "14px", 
            fontWeight: 600, 
            color: theme.text, 
            margin: 0,
            marginBottom: "4px",
          }}>
            {zone.name}
          </h3>
          <div style={{ fontSize: "11px", color: theme.textSecondary }}>
            {zone.points} boundary points
          </div>
        </div>
        <button
          onClick={() => handleDeleteZone(zone.id)}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            fontSize: "12px",
            color: theme.textMuted,
            padding: "4px 8px",
            borderRadius: "4px",
          }}
          onMouseEnter={(e) => e.target.style.color = theme.danger}
          onMouseLeave={(e) => e.target.style.color = theme.textMuted}
        >
          Delete
        </button>
      </div>

      {/* Status Badges */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
        <span style={{
          padding: "4px 10px",
          borderRadius: "6px",
          fontSize: "10px",
          fontWeight: 600,
          background: zone.status === "allowed" ? theme.successLight : theme.dangerLight,
          color: zone.status === "allowed" ? theme.success : theme.danger,
        }}>
          {zone.status === "allowed" ? "Allowed Zone" : "Blacklisted Zone"}
        </span>
        <span style={{
          padding: "4px 10px",
          borderRadius: "6px",
          fontSize: "10px",
          fontWeight: 600,
          background: zone.active ? theme.successLight : theme.warningLight,
          color: zone.active ? theme.success : theme.warning,
        }}>
          {zone.active ? "Active" : "Inactive"}
        </span>
      </div>

      {/* Action Buttons */}
      <div style={{ 
        display: "flex", 
        gap: "8px", 
        borderTop: `1px solid ${theme.border}`, 
        paddingTop: "10px",
        marginTop: "2px",
      }}>
        <button style={{
          flex: 1,
          padding: "6px 12px",
          background: theme.primaryLight,
          color: theme.primary,
          border: "none",
          borderRadius: "6px",
          cursor: "pointer",
          fontSize: "11px",
          fontWeight: 500,
        }}>
          View Details
        </button>
        <button style={{
          flex: 1,
          padding: "6px 12px",
          background: theme.primaryLight,
          color: theme.primary,
          border: "none",
          borderRadius: "6px",
          cursor: "pointer",
          fontSize: "11px",
          fontWeight: 500,
        }}>
          Edit Zone
        </button>
      </div>
    </div>
  );

  return (
    <div style={{
      height: "100vh",
      background: theme.bg,
      padding: "20px 24px",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif",
      color: theme.text,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}>
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        button { transition: all 0.2s ease; cursor: pointer; }
        button:hover { opacity: 0.85; }
        button:active { transform: scale(0.98); }
        input:focus { outline: none; border-color: ${theme.primary} !important; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: ${theme.border}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb { background: ${theme.primary}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: ${theme.primary}; opacity: 0.8; }
      `}</style>

      {/* Header Section */}
      <div style={{ marginBottom: "20px", flexShrink: 0 }}>
        <h1 style={{ fontSize: "24px", fontWeight: 700, marginBottom: "6px", letterSpacing: "-0.3px" }}>
          Zone Management
        </h1>
        <p style={{ fontSize: "13px", color: theme.textSecondary }}>
          Create and manage delivery zones for your business operations
        </p>
      </div>

      {/* Statistics Cards */}
      <div style={{ 
        display: "flex", 
        gap: "14px", 
        marginBottom: "20px", 
        flexWrap: "wrap", 
        flexShrink: 0 
      }}>
        <StatCard label="Total Zones" value={stats.total} color={theme.primary} />
        <StatCard label="Allowed Zones" value={stats.allowed} color={theme.success} />
        <StatCard label="Blacklisted Zones" value={stats.blacklisted} color={theme.danger} />
        <StatCard label="Active Zones" value={stats.active} color={theme.warning} />
      </div>

      {/* Main Content Area */}
      <div style={{ 
        display: "flex", 
        gap: "20px", 
        flex: 1, 
        minHeight: 0,
        overflow: "hidden",
      }}>
        
        {/* Left Panel - Map Section */}
        <div style={{ 
          flex: 1, 
          minWidth: "300px", 
          display: "flex", 
          flexDirection: "column",
          overflow: "hidden",
        }}>
          <div style={{
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: "12px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}>
            {/* Search Bar */}
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${theme.border}`, flexShrink: 0 }}>
              <input
                type="text"
                placeholder="Search location on map..."
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  background: theme.bg,
                  border: `1px solid ${theme.border}`,
                  borderRadius: "8px",
                  fontSize: "12px",
                  color: theme.text,
                }}
              />
              <button style={{
                marginTop: "10px",
                width: "100%",
                padding: "8px 12px",
                background: theme.primaryLight,
                color: theme.primary,
                border: "none",
                borderRadius: "8px",
                fontSize: "12px",
                fontWeight: 500,
              }}>
                Use Current Location
              </button>
            </div>

            {/* Map Placeholder */}
            <div style={{
              flex: 1,
              background: isDark ? "#13161a" : "#e5e7eb",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              minHeight: 0,
            }}>
              <div style={{ 
                textAlign: "center", 
                color: theme.textSecondary,
              }}>
                <div style={{ fontSize: "48px", marginBottom: "12px", opacity: 0.5 }}>🗺️</div>
                <p style={{ fontSize: "13px", fontWeight: 500, marginBottom: "4px" }}>Interactive Map View</p>
                <p style={{ fontSize: "11px" }}>Coordinates: {mapCenter.lat}, {mapCenter.lng}</p>
              </div>
              
              {/* Map Controls */}
              <div style={{
                position: "absolute",
                right: "12px",
                bottom: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}>
                <button style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "6px",
                  background: theme.surface,
                  border: `1px solid ${theme.border}`,
                  fontSize: "16px",
                  fontWeight: 600,
                  color: theme.text,
                }}>+</button>
                <button style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "6px",
                  background: theme.surface,
                  border: `1px solid ${theme.border}`,
                  fontSize: "16px",
                  fontWeight: 600,
                  color: theme.text,
                }}>-</button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Zones List */}
        <div style={{ 
          flex: 1, 
          minWidth: "320px", 
          display: "flex", 
          flexDirection: "column",
          overflow: "hidden",
        }}>
          <div style={{
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: "12px",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
          }}>
            
            {/* Search and Add Button */}
            <div style={{ display: "flex", gap: "12px", marginBottom: "16px", flexShrink: 0 }}>
              <input
                type="text"
                placeholder="Search zones by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  flex: 1,
                  padding: "9px 12px",
                  background: theme.bg,
                  border: `1px solid ${theme.border}`,
                  borderRadius: "8px",
                  fontSize: "12px",
                  color: theme.text,
                }}
              />
              <button
                onClick={handleAddZone}
                style={{
                  padding: "9px 18px",
                  background: theme.primary,
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                + Add Zone
              </button>
            </div>

            {/* Filter Tabs */}
            <div style={{
              display: "flex",
              gap: "8px",
              borderBottom: `1px solid ${theme.border}`,
              paddingBottom: "12px",
              flexWrap: "wrap",
              flexShrink: 0,
            }}>
              <TabButton label="All Zones" id="all" count={stats.total} />
              <TabButton label="Allowed" id="allowed" count={stats.allowed} />
              <TabButton label="Blacklisted" id="blacklisted" count={stats.blacklisted} />
              <TabButton label="Active Only" id="active" count={stats.active} />
            </div>

            {/* Zones List - Scrollable Area */}
            <div style={{ 
              flex: 1, 
              overflowY: "auto", 
              marginTop: "14px",
              paddingRight: "4px",
            }}>
              {filteredZones.length === 0 ? (
                <div style={{ 
                  textAlign: "center", 
                  padding: "48px 20px", 
                  color: theme.textSecondary,
                }}>
                  <p style={{ fontSize: "13px", marginBottom: "4px" }}>No zones found</p>
                  <p style={{ fontSize: "11px" }}>Try adjusting your search or filters</p>
                </div>
              ) : (
                <>
                  <div style={{ 
                    fontSize: "11px", 
                    fontWeight: 600, 
                    color: theme.textSecondary, 
                    marginBottom: "12px", 
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}>
                    {filteredZones.length} Zone{filteredZones.length !== 1 ? 's' : ''}
                  </div>
                  {filteredZones.map(zone => (
                    <ZoneCard key={zone.id} zone={zone} />
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZoneManagement;